/**
 * agentClient.ts — Client for the jonas_agent serverless function.
 *
 * Responsibilities:
 * - Provide `requestAgentResponseNew` that posts a JSON payload with { history }.
 * - Map UiMessageHistoryItem to JonasAgentRequestMessageHistoryItem before sending.
 * - Return the server's JonasAgentResponseBody, performing narrow validation.
 * - Emit logs via `loglevel` with correlation IDs and timings for observability.
 * - Use dependency injection for `fetch` to simplify unit testing.
 */

import { logger, newCid } from "./logger.js";
import {
    type UiMessageHistoryItem,
    type JonasAgentRequestMessageHistoryItem,
    type JonasAgentRequestBody,
    type JonasAgentResponseBody,
    type ContentType,
    isJonasAgentResponseBody,
} from "./types.js";

export type Fetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/**
 * Maps a UiMessageHistoryItem to a JonasAgentRequestMessageHistoryItem.
 * 
 * Mapping rules:
 * - USER with SimpleTextContentBlock -> USER with SimpleTextContentBlock (pass through)
 * - ASSISTANT with UiMessageContentBlock -> Discard (not sent to backend)
 * - ASSISTANT with ErrorContentBlock -> Discard (not sent to backend)
 * - ASSISTANT with NotFoundContentBlock -> ASSISTANT with NotFoundContentBlock (pass through)
 * - ASSISTANT with FullContentBlock[] -> ASSISTANT with IdContentBlock[] (only keep type and id)
 * 
 * @param item The UI message history item to map.
 * @returns The mapped request item, or null if the item should be discarded.
 */
export function mapUiHistoryItemToRequestItem(item: UiMessageHistoryItem): JonasAgentRequestMessageHistoryItem | null {
    if (item.role === 'USER') {
        // USER messages pass through unchanged
        return { role: 'USER', contentBlock: item.contentBlock };
    }

    // ASSISTANT messages
    
    // Case 1: contentBlocks (plural)
    if ('contentBlocks' in item) {
        return {
            role: 'ASSISTANT',
            contentBlocks: item.contentBlocks.map(block => ({
                type: block.type as ContentType,
                id: block.id,
            })),
            nbas: item.nbas?.map(nba => ({ ...nba })) ?? [],
        };
    }
    
    // Case 2: contentBlock (single)
    const block = item.contentBlock;
    
    // Discard UI_MESSAGE and ERROR blocks - they should not be sent to the backend
    if (block.type === 'UI_MESSAGE' || block.type === 'ERROR') {
        return null;
    }
    
    // NOT_FOUND passes through unchanged
    if (block.type === 'NOT_FOUND') {
        return { 
            role: 'ASSISTANT', 
            contentBlock: block,
            nbas: item.nbas?.map(nba => ({ ...nba })) ?? [],
        };
    }
    
    // Unknown block type - discard
    return null;
}

/**
 * Transform UI message history into the backend-accepted JonasAgentRequestBody format.
 * Filters out UI_MESSAGE and ERROR blocks, and converts FullContentBlocks to IdContentBlocks.
 * 
 * @param history The UI message history.
 * @returns The request body for the jonas_agent API.
 */
export function toBackendRequestBody(history: UiMessageHistoryItem[]): JonasAgentRequestBody {
    const mappedHistory: JonasAgentRequestMessageHistoryItem[] = [];
    
    for (const item of history) {
        const mapped = mapUiHistoryItemToRequestItem(item);
        if (mapped !== null) {
            mappedHistory.push(mapped);
        }
    }
    
    return { history: mappedHistory };
}

/**
 * Request a response from the jonas_agent serverless function.
 *
 * Implementation notes:
 * - The endpoint defaults to "/" to align with serverless.yaml in later phases.
 * - The function returns JonasAgentResponseBody which we validate using type guards.
 * - On any network or validation error, we throw an Error so the caller can decide how to present it.
 * 
 * @param history The UI message history to send to the backend.
 * @param fetchFn The fetch function to use (injectable for testing).
 * @param endpoint The endpoint URL to call.
 * @returns The JonasAgentResponseBody from the server.
 */
export async function requestAgentResponseNew(
    history: UiMessageHistoryItem[],
    fetchFn: Fetch = fetch,
    endpoint = "/",
): Promise<JonasAgentResponseBody> {
    const cid = newCid("agent");
    const startedAt = Date.now();
    logger.info("requestAgentResponseNew: start", { cid, endpoint, count: history.length });

    try {
        const backendBody = toBackendRequestBody(history);
        const headers = { "Content-Type": "application/json" };
        logger.debug(cid + " - " + "Request details", { headers, completeBody: backendBody });

        const res = await fetchFn(endpoint, {
            method: "POST",
            headers,
            body: JSON.stringify(backendBody),
        });
        const durationMs = Date.now() - startedAt;

        if (!res.ok) {
            logger.error("requestAgentResponseNew: HTTP error", { cid, status: res.status, statusText: res.statusText, durationMs });
            throw new Error(`Request failed with ${res.status} ${res.statusText}`);
        }

        const data = (await res.json()) as unknown;
        logger.debug(cid + " - " + "Response details", {
            headers: Object.fromEntries(res.headers.entries()),
            completeBody: data,
        });

        // Validate response using type guard
        if (isJonasAgentResponseBody(data)) {
            const content = data.content;
            const responseType = content.type;
            if (responseType === 'ERROR') {
                logger.warn("requestAgentResponseNew: error response", { cid, durationMs, error: content.error });
            } else if (responseType === 'NOT_FOUND') {
                logger.info("requestAgentResponseNew: not found", { cid, durationMs });
            } else {
                logger.info("requestAgentResponseNew: success", { cid, durationMs, type: responseType });
            }
            return data;
        }

        logger.error("requestAgentResponseNew: invalid payload", { cid, durationMs, data });
        throw new Error("Invalid payload from server");
    } catch (err) {
        const durationMs = Date.now() - startedAt;
        logger.error("requestAgentResponseNew: exception", { cid, durationMs, error: String(err) });
        throw err;
    }
}
